// STREAKS
// Upside-only daily-posting streaks, inspired by Duolingo but minus the
// parental guilt. There are no notifications, no "don't break your
// streak!" pop-ups, no streak-freeze items to buy. You post every day,
// you get a time-wallet bonus at each milestone. Miss a day? Streak
// resets to zero, zero penalty. The only thing you lose is upside.
//
// Bonuses are delivered AT THE MOMENT you cross a milestone, not daily,
// so the ceremony is meaningful. Every day after a milestone, the next
// milestone is the carrot.

import { DateTime } from 'luxon';
import { PostData } from '../model/types';
import { supabase } from '../supabaseClient';

/** Milestone day counts and the wallet bonus (in seconds) awarded at each. */
export const STREAK_MILESTONES: Array<{ days: number; bonusSeconds: number; label: string }> = [
  { days: 3,   bonusSeconds: 60 * 60,        label: '🔥 3-day streak: +1h wallet' },
  { days: 7,   bonusSeconds: 3 * 60 * 60,    label: '🔥 7-day streak: +3h wallet' },
  { days: 14,  bonusSeconds: 6 * 60 * 60,    label: '🔥 14-day streak: +6h wallet' },
  { days: 30,  bonusSeconds: 12 * 60 * 60,   label: '🔥 30-day streak: +12h wallet' },
  { days: 60,  bonusSeconds: 24 * 60 * 60,   label: '🔥🔥 60-day streak: +1 day wallet' },
  { days: 100, bonusSeconds: 48 * 60 * 60,   label: '🔥🔥 100-day streak: +2 days wallet' },
  { days: 200, bonusSeconds: 72 * 60 * 60,   label: '🔥🔥🔥 200-day streak: +3 days wallet' },
  { days: 365, bonusSeconds: 7 * 24 * 60 * 60, label: '🔥🔥🔥 1-year streak: +7 days wallet' },
];

/**
 * Compute the current streak for a user from their posts.
 *
 * Legacy path (for UI components that still pass in cached posts).
 * PROBLEM: `posts` in memory resets to near-empty on the 1st of every
 * month because the wipe deletes expired content — which means every
 * user's streak used to reset to zero at midnight UTC on wipe night,
 * and milestone bonuses would replay as the user rebuilt their posts.
 *
 * Prefer `computeStreakFromActivity(userId)` below, which reads from
 * `user_daily_activity` (wipe-proof, counts only, never deleted except
 * when the user themselves is reaped).
 */
export function computeStreak(posts: PostData[], userId: string, now: DateTime = DateTime.utc()): number {
  // Get unique UTC dates the user posted on
  const userPosts = posts.filter((p) => p.user_record_id === userId);
  if (userPosts.length === 0) return 0;

  const postedDates = new Set(
    userPosts
      .map((p) => DateTime.fromISO(p.created_at, { zone: 'utc' }).toISODate())
      .filter(Boolean) as string[]
  );

  // Start from today — if no post today, start from yesterday (streak still active)
  let cursor = now.startOf('day');
  if (!postedDates.has(cursor.toISODate() ?? '')) {
    cursor = cursor.minus({ days: 1 });
  }

  let count = 0;
  while (postedDates.has(cursor.toISODate() ?? '')) {
    count += 1;
    cursor = cursor.minus({ days: 1 });
  }
  return count;
}

/**
 * Wipe-proof streak computation. Reads from `user_daily_activity`
 * (per-user daily counter table maintained by the `bump_activity`
 * trigger — see supabase/migrations/017_metrics.sql). Counts any day
 * with at least one posts/votes/follows action as "active."
 *
 * Async because it hits Supabase. Cache the result in state if you
 * need it inside a render path — do not call on every render.
 */
export async function computeStreakFromActivity(
  userId: string,
  now: DateTime = DateTime.utc(),
): Promise<number> {
  // Fetch the last ~400 days so we cover every milestone including the
  // 365-day one without blowing the payload out.
  const since = now.minus({ days: 400 }).toISODate()!;
  const { data } = await supabase
    .from('user_daily_activity')
    .select('day, posts_count, votes_count, follows_count')
    .eq('user_record_id', userId)
    .gte('day', since);
  if (!data || data.length === 0) return 0;

  const activeDays = new Set(
    data
      .filter((r: any) => (r.posts_count ?? 0) + (r.votes_count ?? 0) + (r.follows_count ?? 0) > 0)
      .map((r: any) => r.day as string),
  );

  let cursor = now.startOf('day');
  if (!activeDays.has(cursor.toISODate() ?? '')) {
    cursor = cursor.minus({ days: 1 });
  }

  let count = 0;
  while (activeDays.has(cursor.toISODate() ?? '')) {
    count += 1;
    cursor = cursor.minus({ days: 1 });
  }
  return count;
}

/**
 * Given the previous streak and the new streak, return the milestone
 * (if any) that was just crossed by this new post.
 * Only returns the HIGHEST milestone crossed to avoid multi-fire on
 * long gaps.
 */
export function getMilestoneCrossed(
  prevStreak: number,
  newStreak: number,
): { days: number; bonusSeconds: number; label: string } | null {
  if (newStreak <= prevStreak) return null;
  const crossed = STREAK_MILESTONES.filter((m) => m.days > prevStreak && m.days <= newStreak);
  if (crossed.length === 0) return null;
  return crossed[crossed.length - 1];
}

/** Human-readable streak chip label. */
export function streakChipLabel(streak: number): string {
  if (streak === 0) return '';
  if (streak >= 365) return `🔥🔥🔥 ${streak}d streak`;
  if (streak >= 100) return `🔥🔥 ${streak}d streak`;
  if (streak >= 7)   return `🔥 ${streak}d streak`;
  return `${streak}d streak`;
}
